/**
 * UpdateIndicator - Indicador de última actualización con contador regresivo
 * Sincronizado con el ciclo real de polling de MarketsList (evento 'marketsUpdated')
 * y respeta el refreshRate configurado por el usuario.
 */
import { useState, useEffect, useRef, memo, useContext } from 'react';
import { ArrowClockwise, Circle } from '@phosphor-icons/react';
import LoadingContext from '../contexts/LoadingContext';
import { useRefreshRate } from './RefreshRateSelector';

// Hook seguro que no falla si el contexto no existe
const useSafeLoading = () => {
  const context = useContext(LoadingContext);
  return context || { 
    globalProgress: 0, 
    isLoading: false, 
    isFullyLoaded: false,
    registerComponent: () => {},
    unregisterComponent: () => {},
    markComponentLoaded: () => {},
    markComponentLoading: () => {},
  };
};

const UpdateIndicator = memo(({ loading: externalLoading, loadingProgress: externalProgress, lastUpdate, enlarged = false }) => {
  const { refreshRate } = useRefreshRate();
  const refreshSeconds = Math.max(1, Math.round((refreshRate || 30000) / 1000));
  const [countdown, setCountdown] = useState(refreshSeconds);
  const intervalRef = useRef(null);
  const hasRegisteredClock = useRef(false);
  const hasReportedClock = useRef(false);
  
  // Usar contexto de carga global de forma segura
  const loadingCtx = useSafeLoading();

  // Gating (jun 2026): durante la PRIMERA carga, el único loader debe ser
  // AppReadyIndicator ("Cargando App"). Este indicador NO muestra "ACTUALIZANDO APP"
  // hasta que la carga inicial haya terminado una vez; a partir de ahí sí aparece en
  // las actualizaciones periódicas. Evita ver dos porcentajes distintos a la vez.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Latch por evento: AppReadyIndicator ("Cargando App") emite este evento cuando la
  // PRIMERA carga termina de verdad (100% real) o se colapsa a "parcial". Solo
  // entonces habilitamos "ACTUALIZANDO APP". Usar isFullyLoaded aquí no vale porque
  // con datos en caché parpadea a true un instante antes del fetch en vivo.
  useEffect(() => {
    const done = () => setInitialLoadDone(true);
    window.addEventListener('kapital:initialLoaderDone', done);
    return () => window.removeEventListener('kapital:initialLoaderDone', done);
  }, []);
  // Fallback: si el safety-timeout forzó componentes, la carga inicial se da por terminada.
  useEffect(() => {
    if (loadingCtx.hasForcedComponents) setInitialLoadDone(true);
  }, [loadingCtx.hasForcedComponents]);
  const initialLoading = loadingCtx.hasStartedLoading && !initialLoadDone;
  
  // Ref estable para el contexto
  const loadingCtxRef = useRef(loadingCtx);
  loadingCtxRef.current = loadingCtx;
  
  // Usar el progreso global del LoadingContext (más preciso que StockContext)
  const loadingProgress = loadingCtx.globalProgress > 0 ? loadingCtx.globalProgress : (externalProgress || 0);
  const loading = loadingCtx.isLoading || externalLoading;
  
  // Reportar que el reloj está listo al montar (solo una vez)
  useEffect(() => {
    const ctx = loadingCtxRef.current;
    
    if (!hasRegisteredClock.current && ctx?.registerComponent) {
      ctx.registerComponent('clock');
      hasRegisteredClock.current = true;
    }
    
    if (!hasReportedClock.current) {
      const timer = setTimeout(() => {
        const currentCtx = loadingCtxRef.current;
        if (!hasReportedClock.current && currentCtx?.markComponentLoaded) {
          currentCtx.markComponentLoaded('clock');
          hasReportedClock.current = true;
          console.log('📊 LoadingContext: Reloj marcado como cargado');
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Contador regresivo — decrementa cada segundo
  // Al llegar a 0 se resetea (el polling real ya se encargará de refrescar; no forzamos nada aquí)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        const newVal = prev - 1;
        if (newVal <= 0) return refreshSeconds;
        return newVal;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refreshSeconds]);
  
  // Sincronizar con el evento real del polling: cada vez que MarketsList completa un batch,
  // reseteamos el contador para que refleje el estado real y no se desalinee.
  useEffect(() => {
    const onSync = () => setCountdown(refreshSeconds);
    window.addEventListener('marketsUpdated', onSync);
    window.addEventListener('resetUpdateCountdown', onSync);
    return () => {
      window.removeEventListener('marketsUpdated', onSync);
      window.removeEventListener('resetUpdateCountdown', onSync);
    };
  }, [refreshSeconds]);

  // Durante la carga inicial no renderizamos nada: el único loader es AppReadyIndicator.
  if (initialLoading) return null;

  const formatCountdown = () => {
    if (countdown >= 60) {
      const minutes = Math.floor(countdown / 60);
      const seconds = (countdown % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    }
    return `${countdown}s`;
  };
  const isPulsing = countdown <= 3;

  // Estilos según tamaño
  const containerClass = enlarged 
    ? `flex items-center gap-6 px-8 py-4 rounded-2xl border-2 transition-all ${
        loading 
          ? 'bg-zinc-500/15 border-zinc-400/60 shadow-lg shadow-zinc-400/20' 
          : 'bg-zinc-900/80 border-gold-500/40 shadow-lg shadow-gold-500/10'
      }`
    : `flex items-center gap-4 px-5 py-2.5 rounded-xl border-2 transition-all ${
        loading 
          ? 'bg-zinc-500/15 border-zinc-400/60 shadow-lg shadow-zinc-400/20' 
          : 'bg-zinc-900/80 border-zinc-600'
      }`;

  return (
    <div className={enlarged ? "" : "flex justify-end mt-3"}>
      <div 
        className={containerClass}
        data-testid="update-indicator"
      >
        {loading ? (
          <>
            <ArrowClockwise size={enlarged ? 32 : 20} className="text-zinc-300 animate-spin" />
            <div className="flex flex-col">
              <span className={`${enlarged ? 'text-xl' : 'text-base'} text-zinc-300 font-bold animate-pulse`}>
                ACTUALIZANDO APP
              </span>
              <div className="flex items-center gap-2 mt-1">
                <div className={`${enlarged ? 'w-40' : 'w-28'} h-2 bg-zinc-700 rounded-full overflow-hidden`}>
                  <div 
                    className="h-full bg-zinc-300 rounded-full transition-all"
                    style={{ width: `${loadingProgress || 0}%` }}
                  />
                </div>
                <span className={`${enlarged ? 'text-lg' : 'text-sm'} text-zinc-300 font-mono font-bold animate-pulse`}>
                  {loadingProgress || 0}%
                </span>
              </div>
            </div>
          </>
        ) : lastUpdate ? (
          <>
            <div className="flex flex-col">
              <span className={`${enlarged ? 'text-lg' : 'text-sm'} text-green-400 font-bold uppercase tracking-wide`}>
                Actualizado
              </span>
              <div className="flex items-center gap-1.5 mt-1">
                <Circle size={enlarged ? 10 : 8} weight="fill" className="text-green-400" />
                <span className={`${enlarged ? 'text-xl' : 'text-base'} text-zinc-200 font-mono`}>
                  {lastUpdate.toLocaleDateString('es-ES')}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Circle size={enlarged ? 10 : 8} weight="fill" className="text-green-400" />
                <span className={`${enlarged ? 'text-xl' : 'text-base'} text-zinc-200 font-mono`}>
                  {lastUpdate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </div>
            </div>
            {/* Separador */}
            <div className={`w-px ${enlarged ? 'h-16' : 'h-10'} bg-zinc-600`}></div>
            {/* Contador próxima actualización */}
            <div className="flex flex-col items-center">
              <span className={`${enlarged ? 'text-sm' : 'text-[10px]'} text-zinc-400 uppercase tracking-wider`}>
                Próxima en
              </span>
              <span className={`${enlarged ? 'text-4xl' : 'text-lg'} ${isPulsing ? 'text-gold-300 animate-pulse' : 'text-gold-400'} font-mono font-bold`}
                    style={enlarged ? { textShadow: '0 0 15px rgba(251, 191, 36, 0.4)' } : {}}>
                {formatCountdown()}
              </span>
            </div>
          </>
        ) : (
          <>
            <Circle size={enlarged ? 16 : 12} weight="fill" className="text-zinc-500" />
            <span className={`${enlarged ? 'text-xl' : 'text-base'} text-zinc-500`}>Sin datos</span>
          </>
        )}
      </div>
    </div>
  );
});

UpdateIndicator.displayName = 'UpdateIndicator';

export default UpdateIndicator;
